import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  useKrakenState,
  useKrakenEstimate,
  useEngageKraken,
  useMyFleet,
  useMyMissions,
  type HuntThreatDto,
  type MissionDto,
} from '@/api/hooks'
import { CloseIcon, RumIcon, StatIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import { RationsLabel, ShipPickerList } from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { missionFleetCap } from '@/lib/fleetGroups'
import { TripDuration, ConvoyPace } from '@/components/game/map/tripDuration'
import { BeastPortrait } from '@/components/game/kraken/BeastPortrait'
import { HoldToConfirm } from '@/components/game/kraken/HoldToConfirm'

/**
 * Le panneau de chasse : le seul écran du jeu où l'on mise sa flotte entière.
 *
 * Panneau DÉDIÉ et non une variante du panneau d'île, parce que la cible n'en est
 * pas une : ni brouillard, ni commerce, ni fiche de renseignement, et une seule
 * action possible. Élargir `SidePanel` en union discriminée aurait contaminé la
 * carte, le zoom, le focus et le lien profond pour une seule surface.
 *
 * ⚠️ Aucun pourcentage de chance de victoire n'est affiché, nulle part, et ce
 * n'est pas un oubli (fiche 32, décision ⑩) : le serveur n'en expose pas et un
 * test verrouille son absence ici. Ce qu'on montre est mécanique et vérifiable :
 * ses dés, son armure, ses points de coque, et le verdict binaire de ricochet.
 */
export function KrakenPanel({
  onCleared,
  variant = 'panel',
}: {
  onCleared: () => void
  variant?: 'panel' | 'sheet'
}) {
  const { data: state } = useKrakenState()
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const engage = useEngageKraken()
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const hunt = state?.hunt ?? null
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  // Un engagement à la fois. Le verrou est par TYPE et non par île : la cible
  // n'étant pas une île, un verrou par destination ne se déclencherait jamais
  // (côté serveur non plus, d'où `hasInProgressOfType`).
  const ongoing: MissionDto | null =
    missionsData?.missions.find((m) => m.status === 'in_progress' && m.type === 'kraken_hunt') ?? null

  const estimate = useKrakenEstimate(selectedIds, selectedIds.length > 0 && ongoing === null)

  const shell =
    variant === 'sheet'
      ? 'p-2 space-y-3'
      : 'rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3'

  if (!hunt || !state) {
    return (
      <div className={shell}>
        <p className="text-sm italic text-ocean-300">Aucune chasse ouverte pour l'instant.</p>
      </div>
    )
  }

  const threat = hunt.threat
  const armure = threat.coreArmure
  // Perce collective, PAR PHASE : le meilleur navire perce, ou personne. C'est le
  // seul « vas-tu y arriver » qu'on affiche, parce qu'il est certain.
  //
  // On compte AUSSI les coques qui percent, et ce n'est pas décoratif : une seule
  // suffit à ouvrir la phase, mais elle peut couler. Tant qu'il en reste une, la
  // volée mord ; quand la dernière tombe, la phase meurt pour le reste de la
  // bataille. Ce compte est donc la marge de sécurité du capitaine.
  const pierce = selected.reduce(
    (acc, s) => ({
      bordee: Math.max(acc.bordee, s.combatStats.bordee),
      abordage: Math.max(acc.abordage, s.combatStats.abordage),
      bordeeCount: acc.bordeeCount + (s.combatStats.bordee >= armure ? 1 : 0),
      abordageCount: acc.abordageCount + (s.combatStats.abordage >= armure ? 1 : 0),
    }),
    { bordee: 0, abordage: 0, bordeeCount: 0, abordageCount: 0 },
  )
  const rhumAvailable = fleet?.resources?.rum ?? 0
  const rhumNeeded = estimate.data?.rhumNeeded ?? 0
  const notEnoughRhum = selected.length > 0 && rhumNeeded > rhumAvailable
  const overCap = selected.length > fleetCap

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  return (
    <div className={shell} data-testid="kraken-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          {/* Nomme l'écran, comme les autres launchers. Surtout pas une phrase
              d'ambiance : « elle est remontée » ne veut rien dire d'une armada
              qui sort de la brume, et l'ambiance a déjà sa place, la citation
              d'apparition juste sous le portrait. */}
          <p className="text-[11px] uppercase tracking-[0.16em] text-ocean-300">Chasse légendaire</p>
          <h3 className="font-display text-xl text-sand-300">{state.archetype.label}</h3>
        </div>
        <button onClick={onCleared} className="text-ocean-300 transition-colors hover:text-ocean-100" aria-label="Fermer">
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid place-items-center">
        <BeastPortrait archetypeKey={hunt.archetypeKey} size={168} />
      </div>
      <p className="text-center text-xs italic leading-snug text-ocean-200">« {state.archetype.sighting} »</p>

      <BeastStats threat={threat} />

      <div className="space-y-1.5 rounded-lg border border-sand-500/15 bg-ocean-900/50 p-2 text-xs">
        <PierceRow
          label="Ta perce en bordée"
          value={pierce.bordee}
          wall={armure}
          count={pierce.bordeeCount}
          shown={selected.length > 0}
        />
        <PierceRow
          label="Ta perce à l'abordage"
          value={pierce.abordage}
          wall={armure}
          count={pierce.abordageCount}
          shown={selected.length > 0}
        />
        <div className="mt-1.5 flex justify-between border-t border-sand-500/15 pt-1.5 text-ocean-300">
          <RationsLabel />
          <span
            className={`inline-flex items-center gap-1 tabular-nums ${notEnoughRhum ? 'font-semibold text-red-400' : 'text-sand-300'}`}
          >
            ≈ {rhumNeeded} <RumIcon className="h-3.5 w-3.5" />
            {notEnoughRhum && <span className="ml-1">(banque : {rhumAvailable})</span>}
          </span>
        </div>
        <div className="flex justify-between text-ocean-300">
          <span>Aller et retour</span>
          {estimate.data ? <TripDuration seconds={estimate.data.durationSeconds} /> : <span className="text-ocean-400">—</span>}
        </div>
        <ConvoyPace ships={selected} tripSeconds={estimate.data?.durationSeconds} />
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-ocean-300">Tentatives restantes</span>
        <AttemptPips left={hunt.attemptsLeft} total={state.archetype.maxAttempts} />
      </div>

      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />

      {estimate.data?.willRicochet && (
        <p className="border-l-2 border-rum-500 pl-2 text-xs italic text-rum-500">
          Aucun de tes navires ne perce son armure ({armure}). Tes boulets ricocheront, et pas une écaille ne tombera.
        </p>
      )}

      <p className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs leading-snug text-red-200">
        <StatIcon name="armure" className="mt-0.5 h-4 w-4 shrink-0 text-red-300" />
        <span>
          <strong className="font-display text-red-100">Tout ou rien.</strong> Si la bête est encore debout au bout des
          dix manches, aucune coque ne rentre. Pas les survivantes, pas une seule.
        </span>
      </p>

      {ongoing ? (
        <p className="text-xs text-sand-300">Ta flotte est déjà en route vers la bête.</p>
      ) : (
        <>
          <HoldToConfirm
            label="Maintenir pour engager la flotte"
            holdingLabel="Ne relâche pas…"
            disabled={engage.isPending || selected.length === 0 || notEnoughRhum || overCap || hunt.attemptsLeft <= 0}
            onConfirm={() => {
              const shipIds = selected.map((s) => s.id)
              engage.mutate(
                { shipIds },
                {
                  onSuccess: () => {
                    toast.success(`${shipIds.length} navire(s) en route vers ${state.archetype.label}.`)
                    setSelectedIds([])
                  },
                  onError: (err) => toast.error(err.message || 'Engagement impossible'),
                },
              )
            }}
          />
          <p id="hold-hint" className="text-center text-[11px] text-ocean-400">
            {selected.length > 0
              ? `${selected.length} coque(s) misée(s). Maintiens le bouton pour larguer les amarres.`
              : 'Choisis tes coques, puis maintiens le bouton.'}
          </p>
        </>
      )}
    </div>
  )
}

/** Les quatre chiffres de la bête, à égalité : armure, coque, bordée, abordage. */
function BeastStats({ threat }: { threat: HuntThreatDto }) {
  const cells = [
    { k: 'Armure', v: threat.coreArmure, hot: false },
    { k: 'Coque', v: threat.coreHp.toLocaleString('fr-FR'), hot: true },
    { k: 'Bordée', v: threat.bordee, hot: false },
    { k: 'Abordage', v: threat.abordage, hot: false },
  ]
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {cells.map((c) => (
        <div
          key={c.k}
          className={`rounded-lg border p-1.5 text-center ${
            c.hot ? 'border-sand-500/35 bg-rum-700/20' : 'border-ocean-500/20 bg-ocean-900/50'
          }`}
        >
          <span className="block text-[9px] uppercase tracking-wider text-ocean-300">{c.k}</span>
          <span className="block font-display text-base tabular-nums text-sand-200">{c.v}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Ta perce contre son armure, et combien de coques la tiennent.
 *
 * Le verdict est binaire et certain : au-dessus tu entames, en dessous tu
 * ricoches. Rien à voir avec une probabilité. Le COMPTE ajoute la seule nuance
 * qui manque : une coque suffit à ouvrir la phase, mais si elle coule la phase se
 * referme, donc quatre coques qui percent valent bien mieux qu'une.
 */
function PierceRow({
  label,
  value,
  wall,
  count,
  shown,
}: {
  label: string
  value: number
  wall: number
  /** Coques sélectionnées qui percent réellement dans cette phase. */
  count: number
  shown: boolean
}) {
  const ok = value >= wall
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-ocean-300">{label}</span>
      {shown ? (
        <span className="text-right">
          <span className={`tabular-nums font-semibold ${ok ? 'text-sand-300' : 'text-rum-500'}`}>
            {value} contre {wall}
          </span>
          <span className={`ml-1.5 tabular-nums ${ok ? 'text-ocean-300' : 'text-rum-500'}`}>
            {ok ? `· ${count} coque${count > 1 ? 's' : ''}` : '· aucune coque ne perce'}
          </span>
        </span>
      ) : (
        <span className="tabular-nums text-ocean-400">— contre {wall}</span>
      )}
    </div>
  )
}

/**
 * Le budget de tentatives : des pastilles qui s'éteignent, ET le compte en
 * clair. Les pastilles seules étaient ambiguës — quatre carrés pleins ne disent
 * pas si c'est « quatre encore » ou « la dernière ». Le chiffre tranche, les
 * pastilles donnent la proportion d'un coup d'œil.
 */
export function AttemptPips({ left, total }: { left: number; total: number }) {
  const last = left === 1
  return (
    <span className="inline-flex items-center gap-2" title={`${left} tentative(s) restante(s) sur ${total}`}>
      <span aria-hidden="true" className="inline-flex items-center gap-1">
        {Array.from({ length: total }, (_, i) => (
          <i
            key={i}
            className={`h-2.5 w-2.5 rounded-[3px] border ${
              i < left ? 'border-sand-300/60 bg-sand-300/75' : 'border-ocean-400/40 bg-transparent'
            }`}
          />
        ))}
      </span>
      <span className={`tabular-nums font-semibold ${last ? 'text-red-300' : 'text-sand-200'}`}>
        {left === 0 ? 'aucune' : last ? 'la dernière' : `${left} sur ${total}`}
      </span>
    </span>
  )
}
